'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LinkSimple, Copy, Clock, Shield, SignOut } from 'phosphor-react'
import Components from '../components'
import { generateTempUrls } from '../utils/tempAccess'
import { verifyPassword } from '../utils/passwordHash'
import { TokenGenerator } from '../utils/passwordManager'

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminPassword, setAdminPassword] = useState('')
  const [tempUrls, setTempUrls] = useState<Record<string, string>>({})
  const [tokenUrls, setTokenUrls] = useState<Record<string, string>>({})

  const handleAdminLogin = () => {
    if (verifyPassword(adminPassword, 'admin')) {
      setIsAuthenticated(true)
      setAdminPassword('')
      console.log('✅ Admin access granted')
    } else {
      console.log('❌ Invalid admin password')
      setAdminPassword('')
    }
  }

  const handleAdminLogout = () => {
    setIsAuthenticated(false)
    setTempUrls({})
    setTokenUrls({})
    console.log('🔒 Admin access revoked')
  }

  const generateTempAccessUrls = () => {
    const urls = generateTempUrls()
    setTempUrls(urls)
  }

  const generateTokenUrls = () => {
    try {
      const urls = {
        token14Days: TokenGenerator.generate14DayToken(),
        token21Days: TokenGenerator.generate21DayToken(),
        token30Days: TokenGenerator.generate30DayToken()
      }
      setTokenUrls(urls)
    } catch (error) {
      console.error('Error generating token URLs:', error)
      alert('Error generating token URLs. Please try again.')
    }
  }

  const [copyStatus, setCopyStatus] = useState<Record<string, 'idle' | 'success' | 'error'>>({})

  const copyToClipboard = async (text: string, urlType: string) => {
    // Validate input
    if (!text || typeof text !== 'string') {
      setCopyStatus(prev => ({ ...prev, [urlType]: 'error' }))
      setTimeout(() => setCopyStatus(prev => ({ ...prev, [urlType]: 'idle' })), 2000)
      return
    }

    try {
      // Try modern clipboard API first (most secure)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        setCopyStatus(prev => ({ ...prev, [urlType]: 'success' }))
        setTimeout(() => setCopyStatus(prev => ({ ...prev, [urlType]: 'idle' })), 2000)
        return
      }
      
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      textArea.style.opacity = '0'
      textArea.setAttribute('readonly', '')
      textArea.setAttribute('aria-hidden', 'true')
      
      document.body.appendChild(textArea)
      
      // Select and copy
      textArea.select()
      textArea.setSelectionRange(0, 99999) // For mobile devices
      
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      
      if (successful) {
        setCopyStatus(prev => ({ ...prev, [urlType]: 'success' }))
        setTimeout(() => setCopyStatus(prev => ({ ...prev, [urlType]: 'idle' })), 2000)
      } else {
        throw new Error('execCommand failed')
      }
      
    } catch (err) {
      console.error('Failed to copy: ', err)
      setCopyStatus(prev => ({ ...prev, [urlType]: 'error' }))
      setTimeout(() => setCopyStatus(prev => ({ ...prev, [urlType]: 'idle' })), 2000)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--brutal-paper)]">
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-6 px-4 md:px-6 flex flex-col min-h-screen">
        <div className="max-w-4xl mx-auto flex-1 flex flex-col">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-orange-600 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-[var(--brutal-ink)]" />
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--brutal-ink)] mb-2 leading-tight">
                Admin Panel
              </h1>
              <p className="text-[var(--brutal-muted)] text-lg">
                Secure temporary access URL generation
              </p>
            </div>
          </div>

          {/* Debug section */}
          <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4 mb-6">
            <h3 className="text-[var(--brutal-ink)] font-medium mb-3">Debug Tools</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  console.log('🧪 Testing token generation...')
                  const testUrl = (window as any).testTempToken()
                  console.log('🧪 Test URL:', testUrl)
                  alert(`Test URL generated: ${testUrl}`)
                }}
                size="sm"
                variant="outline"
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
              >
                Test Token Generation
              </Button>
              <Button
                onClick={() => {
                  (window as any).clearAllTempTokens()
                  alert('All tokens cleared!')
                }}
                size="sm"
                variant="outline"
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
              >
                Clear All Tokens
              </Button>
              <Button
                onClick={() => {
                  (window as any).cleanupTempTokens()
                  alert('Token cleanup completed!')
                }}
                size="sm"
                variant="outline"
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)]"
              >
                Cleanup Tokens
              </Button>
            </div>
          </div>

          {!isAuthenticated ? (
            /* Admin Login Form */
            <Card className="brutal-panel p-8 max-w-md mx-auto">
              <div className="text-center mb-6">
                <Shield className="w-12 h-12 text-[var(--brutal-accent)] mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-[var(--brutal-ink)] mb-2">Admin Authentication</h2>
                <p className="text-[var(--brutal-muted)] text-sm">
                  Enter admin password to access URL generation tools
                </p>
              </div>
              
              <div className="space-y-4">
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter admin password..."
                  className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)]"
                  onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                />
                <Button
                  onClick={handleAdminLogin}
                  className="w-full brutal-btn text-[var(--brutal-ink)] border-0"
                >
                  Login
                </Button>
              </div>
            </Card>
          ) : (
            /* Admin Dashboard */
            <div className="space-y-6">
              {/* Header with Logout */}
              <Card className="brutal-panel p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="w-6 h-6 text-[var(--brutal-success)] mr-3" />
                    <div>
                      <h2 className="text-[var(--brutal-ink)] font-semibold">Admin Dashboard</h2>
                      <p className="text-[var(--brutal-success)] text-sm">🔓 Admin access active</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleAdminLogout}
                    variant="outline"
                    className="bg-red-500/20 border-red-400/30 text-[var(--brutal-accent)] hover:bg-red-500/30"
                  >
                    <SignOut weight="duotone" className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </Card>

              {/* URL Generator */}
              <Card className="brutal-panel p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[var(--brutal-ink)] font-semibold text-xl flex items-center">
                    <LinkSimple weight="duotone" className="w-5 h-5 mr-3 text-[var(--brutal-accent)]" />
                    Temporary Access URLs
                  </h3>
                  <Button
                    onClick={() => {
                      generateTempAccessUrls()
                      generateTokenUrls()
                    }}
                    className="brutal-btn text-[var(--brutal-ink)] border-0"
                  >
                    Generate URLs
                  </Button>
                </div>
                
                {(Object.keys(tempUrls).length > 0 || Object.keys(tokenUrls).length > 0) && (
                  <div className="space-y-4">
                    {/* Token URLs (14, 21, 30 days) */}
                    {Object.keys(tokenUrls).length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">14 Days</span>
                          <div className="flex items-center text-[var(--brutal-ink)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">14 days</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tokenUrls.token14Days || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tokenUrls.token14Days, 'token14Days')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.token14Days === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.token14Days === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.token14Days === 'success' || copyStatus.token14Days === 'error'}
                          >
                            {copyStatus.token14Days === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.token14Days === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">100 uses • 2 weeks access</p>
                      </div>

                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">21 Days</span>
                          <div className="flex items-center text-[var(--brutal-success)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">21 days</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tokenUrls.token21Days || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tokenUrls.token21Days, 'token21Days')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.token21Days === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.token21Days === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.token21Days === 'success' || copyStatus.token21Days === 'error'}
                          >
                            {copyStatus.token21Days === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.token21Days === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">100 uses • 3 weeks access</p>
                      </div>

                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">30 Days</span>
                          <div className="flex items-center text-[var(--brutal-ink)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">30 days</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tokenUrls.token30Days || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tokenUrls.token30Days, 'token30Days')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.token30Days === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.token30Days === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.token30Days === 'success' || copyStatus.token30Days === 'error'}
                          >
                            {copyStatus.token30Days === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.token30Days === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">100 uses • 1 month access</p>
                      </div>
                    </div>
                    )}

                    {/* Temporary Access URLs */}
                    {Object.keys(tempUrls).length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">Quick Demo</span>
                          <div className="flex items-center text-[var(--brutal-ink)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">1 hour</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tempUrls.quickDemo || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tempUrls.quickDemo, 'quickDemo')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.quickDemo === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.quickDemo === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.quickDemo === 'success' || copyStatus.quickDemo === 'error'}
                          >
                            {copyStatus.quickDemo === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.quickDemo === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">3 uses • Perfect for brief demos</p>
                      </div>

                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">Client Review</span>
                          <div className="flex items-center text-[var(--brutal-ink)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">24 hours</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tempUrls.clientReview || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tempUrls.clientReview, 'clientReview')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.clientReview === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.clientReview === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.clientReview === 'success' || copyStatus.clientReview === 'error'}
                          >
                            {copyStatus.clientReview === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.clientReview === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">10 uses • Ideal for client feedback</p>
                      </div>

                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">Project Review</span>
                          <div className="flex items-center text-[var(--brutal-success)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">3 days</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tempUrls.projectReview || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tempUrls.projectReview, 'projectReview')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.projectReview === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.projectReview === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.projectReview === 'success' || copyStatus.projectReview === 'error'}
                          >
                            {copyStatus.projectReview === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.projectReview === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">20 uses • Extended project discussions</p>
                      </div>

                      <div className="bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[var(--brutal-ink)] font-medium">Extended Review</span>
                          <div className="flex items-center text-[var(--brutal-ink)]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span className="text-sm">7 days</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={tempUrls.extendedReview || ''}
                            readOnly
                            className="text-xs bg-[var(--brutal-kraft)] border-[var(--brutal-ink)] text-[var(--brutal-ink)]"
                          />
                          <Button
                            onClick={() => copyToClipboard(tempUrls.extendedReview, 'extendedReview')}
                            size="sm"
                            variant="outline"
                            className={`bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] ${
                              copyStatus.extendedReview === 'success' ? 'bg-green-500/20 border-green-400' : 
                              copyStatus.extendedReview === 'error' ? 'bg-red-500/20 border-red-400' : ''
                            }`}
                            disabled={copyStatus.extendedReview === 'success' || copyStatus.extendedReview === 'error'}
                          >
                            {copyStatus.extendedReview === 'success' ? (
                              <span className="text-[var(--brutal-success)]">✓</span>
                            ) : copyStatus.extendedReview === 'error' ? (
                              <span className="text-red-800">✗</span>
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-[var(--brutal-muted)] text-xs mt-2">50 uses • Comprehensive stakeholder review</p>
                      </div>
                    </div>
                    )}
                    
                    <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-4">
                      <p className="text-[var(--brutal-ink)] text-sm text-center">
                        <strong>Security Note:</strong> These URLs bypass the main password and expire automatically. 
                        Share them securely with clients or stakeholders only.
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
